import React, { useState, useRef, useEffect } from "react";
import { Form, Button, Spinner, Card, Badge } from "react-bootstrap";
import axios from "axios";
import EchoAvatar from "../avatars-256X256/Echo.png";
import Swal from "sweetalert2";
import { FUNCTION_URLS } from "../../../config/functionUrls";

// Sub-componente para la tarjeta estilo Red Social de Echo
const EchoMessageCard = ({ data, distributed, onDistribute, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textLimit = 150;
  const isLongText = data.text.length > textLimit;

  return (
    <div className="echo-social-card shadow-lg animate__animated animate__fadeInUp" style={{
      background: '#1a1a1a',
      borderRadius: '24px',
      overflow: 'hidden',
      border: '1px solid #333',
      maxWidth: '95%',
      marginBottom: '20px'
    }}>
      {/* 1. Header: Título y Categoría */}
      <div className="p-4 pb-2">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <h3 className="text-white fw-bold m-0" style={{ fontSize: '20px' }}>{data.title}</h3>
          <Badge bg={data.priority === 'high' ? 'danger' : (data.priority === 'medium' ? 'warning' : 'info')} className="text-uppercase px-2 py-1" style={{ fontSize: '9px' }}>
            {data.type}
          </Badge>
        </div>
        
        {/* 2. Cuerpo: Texto con "Ver más" */}
        <div className="text-white-50 small mb-3" style={{ lineHeight: '1.6' }}>
          {isExpanded || !isLongText ? (
            <p className="m-0" style={{ whiteSpace: 'pre-line' }}>{data.text}</p>
          ) : (
            <p className="m-0">
              {data.text.substring(0, textLimit)}...
              <span 
                onClick={() => setIsExpanded(true)} 
                style={{ color: '#3b82f6', cursor: 'pointer', fontWeight: 'bold', marginLeft: '5px' }}
              >
                Ver más
              </span>
            </p>
          )}
          {isExpanded && isLongText && (
            <span 
              onClick={() => setIsExpanded(false)} 
              style={{ color: '#3b82f6', cursor: 'pointer', fontWeight: 'bold', display: 'block', marginTop: '5px' }}
            >
              Ver menos
            </span>
          )}
        </div>
      </div>

      {/* 3. Visual: Imagen Vertical 9:16 - Tamaño reducido y centrado */}
      <div style={{ 
        position: 'relative', 
        width: '420px', 
        margin: '0 auto',
        minHeight: '400px', 
        maxHeight: '740px', 
        backgroundColor: '#000', 
        overflowY: 'auto', 
        borderRadius: '16px',
        border: '1px solid #333' 
      }}>
          <img
            src={data.imageUrl}
            style={{ width: '100%', height: 'auto', display: 'block' }}
            alt="Visual Content"
            onError={(e) => {
              if (data.imageUrlFallback && e.currentTarget.src !== data.imageUrlFallback) {
                e.currentTarget.src = data.imageUrlFallback;
              }
            }}
          />
      </div>

      {/* 4. Footer: Acción de Distribución */}
      <div className="p-3 border-top border-secondary bg-dark d-flex justify-content-between align-items-center">
          <small className="text-muted fst-italic" style={{ fontSize: '11px' }}>
             {distributed ? '✅ Distribuido a todos' : 'Revisión administrativa'}
          </small>
          <Button 
             variant={distributed ? "success" : "primary"} 
             size="sm" 
             className="rounded-pill px-4 fw-bold"
             style={{ fontSize: '12px' }}
             onClick={() => !distributed && onDistribute()}
             disabled={loading || distributed}
          >
              {distributed ? 'Distribuido' : 'Publicar Ahora'}
          </Button>
      </div>
    </div>
  );
};

const EchoChat = ({ usuario }) => {
  const [messages, setMessages] = useState([
    { 
      sender: 'echo', 
      text: 'Hola, soy Echo. Soy tu asistente de comunicaciones corporativas. ¿Qué evento o noticia te gustaría anunciar hoy?',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [event, setEvent] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateCommunication = async () => {
    if (!event.trim()) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [...prev, { sender: 'user', text: event, timestamp }]);
    setLoading(true);
    const intent = event;
    setEvent('');

    try {
      // 1. Generar contenido y prompt desde Echo
      const commRes = await axios.post("https://echo-generatecommunication-pdgfj3iftq-uc.a.run.app", {
        event: intent,
        companyName: "Neurum OS"
      });
      const commData = commRes.data;

      // 2. Generar imagen usando Nano Banana
      const imgRes = await axios.post("https://echo-generateimage-pdgfj3iftq-uc.a.run.app", {
        prompt: commData.imagePrompt,
        dni: usuario?.dni || "WEB_ADMIN"
      });
      
      const finalData = { ...commData, imageUrl: imgRes.data.imageUrl };

      setMessages(prev => [...prev, { 
        sender: 'echo', 
        text: JSON.stringify(finalData),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isData: true
      }]);

    } catch (error) {
      console.error("Error en Echo Chat:", error);
      setMessages(prev => [...prev, { 
        sender: 'echo', 
        text: "Lo siento, ocurrió un error al generar el comunicado. Por favor intenta de nuevo.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleDistribute = async (data, msgIdx) => {
    const result = await Swal.fire({
      title: '¿Confirmar distribución?',
      text: `Se enviará este comunicado a todos los colaboradores de la empresa.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, distribuir ahora',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await axios.post(FUNCTION_URLS.echoDistributeAnnouncement, {
          announcement: data,
          ruc: usuario.ruc,
          senderDni: usuario.dni
        });

        Swal.fire('¡Éxito!', 'El comunicado ha sido distribuido a todos los colaboradores.', 'success');
        
        // Actualizar el mensaje para mostrar que fue distribuido
        setMessages(prev => {
            const newMsgs = [...prev];
            newMsgs[msgIdx] = { ...newMsgs[msgIdx], distributed: true };
            return newMsgs;
        });

      } catch (error) {
        console.error("Error distribuyendo:", error);
        Swal.fire('Error', 'No se pudo completar la distribución masiva.', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const styles = {
    container: {
      maxWidth: '850px',
      margin: '20px auto',
      height: '1500px',
      background: '#0a0a0a',
      borderRadius: '30px',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #222',
      overflow: 'hidden',
      fontFamily: "'Outfit', sans-serif",
      boxShadow: '0 30px 60px rgba(0,0,0,0.5)'
    },
    header: {
      padding: '25px 30px',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e1b4b 100%)',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      borderBottom: '1px solid #222'
    },
    avatar: {
      width: '60px',
      height: '60px',
      borderRadius: '18px',
      background: '#000',
      padding: '2px',
      border: '2px solid rgba(255,255,255,0.1)'
    },
    messages: {
      flex: 1,
      overflowY: 'auto',
      padding: '30px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    inputArea: {
      padding: '25px 30px',
      borderTop: '1px solid #222',
      background: '#111'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <img src={EchoAvatar} alt="Echo" style={styles.avatar} />
        <div>
          <h5 className="m-0 text-white fw-bold">Echo • Comunicados AI</h5>
          <p className="m-0 text-white-50 small">Generador de Identidad y Cultura Organizacional</p>
        </div>
      </div>

      <div style={styles.messages}>
        {messages.map((msg, idx) => {
          if (msg.isData) {
            const data = JSON.parse(msg.text);
            return (
              <EchoMessageCard 
                key={idx} 
                data={data} 
                distributed={msg.distributed} 
                onDistribute={() => handleDistribute(data, idx)}
                loading={loading}
              />
            );
          }

          return (
            <div key={idx} className={`d-flex ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
              <div style={{
                padding: '12px 20px',
                borderRadius: '18px',
                maxWidth: '75%',
                fontSize: '14.5px',
                background: msg.sender === 'user' ? '#2563eb' : '#222',
                color: 'white',
                border: msg.sender === 'user' ? 'none' : '1px solid #333'
              }}>
                {msg.text}
                <div style={{ fontSize: '10px', opacity: 0.4, textAlign: 'right', marginTop: '5px' }}>{msg.timestamp}</div>
              </div>
            </div>
          );
        })}
        {loading && (
            <div className="d-flex justify-content-start align-items-center gap-3">
                <Spinner animation="grow" variant="primary" size="sm" />
                <span className="text-muted small">Echo está diseñando tu comunicado con Nano Banana...</span>
            </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputArea}>
        <Form onSubmit={(e) => { e.preventDefault(); generateCommunication(); }}>
          <div className="d-flex gap-3 mt-2">
            <Form.Control
              className="bg-dark text-white border-secondary rounded-pill px-4 py-2"
              placeholder="Escribe el evento (ej: Anuncio de vacaciones, Alerta de mantenimiento)..."
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              disabled={loading}
              style={{ flex: 1 }}
            />
            <Button 
                variant="primary" 
                className="rounded-pill px-4" 
                onClick={generateCommunication}
                disabled={loading || !event.trim()}
            >
              Generar
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default EchoChat;
